import { Component, DestroyRef, OnDestroy, OnInit, Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { startWith } from 'rxjs';
import { CreateManageUserAsync } from 'src/app/store/createManageUser/async';
import {
  errorsfetchingUserInfo,
  errorsUpdatingUser,
  fetchingUserInfo,
  getUserLists,
  getUsesrlistErrors,
  isFetchingUserLists,
  selectedUserInfo,
  updatingUserInfo,
  updatingUserStatus
} from 'src/app/store/createManageUser/selector';
import { IAdminUserList, ICreateManageUserState, IUserInfoObj, IUserList } from 'src/app/store/createManageUser/state';
import { Lux } from '@roosevelt/common-ui-lib/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';

@Component({
    selector: 'bmt-manage-user',
    templateUrl: './manage-user.component.html',
    styleUrls: ['./manage-user.component.scss'],
    standalone: false
})
export class ManageUserComponent implements OnInit, OnDestroy {
  luxId = 'BMT-manage-user' + this.constructor.name;
  private readonly destroyRef = inject(DestroyRef);

  private readonly usersListResponse = toSignal(this.lux.get(getUserLists, this.luxId), {
    initialValue: { pagination: { totalRecords: 0 }, users: [] } as IAdminUserList
  });
  readonly fetchingUserErrors = toSignal(this.lux.get(getUsesrlistErrors, this.luxId), { initialValue: '' });
  readonly isFetchingUserList = toSignal(this.lux.get(isFetchingUserLists, this.luxId), { initialValue: false });
  readonly updatingUserInfo = toSignal(this.lux.get(updatingUserInfo, this.luxId), { initialValue: false });
  readonly updatingUserStatus = toSignal(this.lux.get(updatingUserStatus, this.luxId), { initialValue: '' });
  readonly errorsUpdatingUser = toSignal(this.lux.get(errorsUpdatingUser, this.luxId), { initialValue: '' });
  readonly fetchingUserInfo = toSignal(this.lux.get(fetchingUserInfo, this.luxId), { initialValue: false });
  readonly selectedUserInfo = toSignal(this.lux.get(selectedUserInfo, this.luxId), { initialValue: [] as IUserInfoObj[] });
  readonly errorsfetchingUserInfo = toSignal(this.lux.get(errorsfetchingUserInfo, this.luxId), { initialValue: '' });
  readonly users = computed(() => this.usersListResponse()?.users || []);
  readonly totalUsers = computed(() => this.usersListResponse()?.pagination?.totalRecords || 0);
  readonly manageUserInfo = signal<IUserList | null>(null);
  readonly formSnapShot = signal('');
  readonly areThereAnyChanges: Signal<boolean>;

  readonly currentPage = signal(0);
  defaultPageSize = 25;
  selectedIndex = 0;
  readonly sortField = signal('');
  readonly sortOrder = signal<'ASC' | 'DESC'>('ASC');
  private readonly updateUserFormValue: Signal<unknown>;
  updateUserForm: UntypedFormGroup;

  constructor(private readonly router: Router,
              private readonly fb: UntypedFormBuilder,
              private readonly lux: Lux<{ createManageUserState: ICreateManageUserState }>,
              private readonly createManageUserAsync: CreateManageUserAsync) {
    this.updateUserForm = this.createUpdateUserForm();
    this.updateUserFormValue = toSignal(this.updateUserForm.valueChanges.pipe(startWith(this.updateUserForm.value)), {
      initialValue: this.updateUserForm.value
    });
    this.areThereAnyChanges = computed(() => this.updateUserForm.valid
      && this.formSnapShot() !== JSON.stringify(this.updateUserFormValue()));

    effect(() => {
      const updateStatus = this.updatingUserStatus();
      if (updateStatus && updateStatus !== 'user data updated') {
        const currentPage = untracked(this.currentPage);
        untracked(() => this.getUsersList(currentPage > 1 ? (currentPage - 1) * this.defaultPageSize : 0));
      }
    });

    effect(() => {
      const currentUser = this.manageUserInfo();
      const currentUserInfo = this.selectedUserInfo()[0];

      if (!currentUser || this.fetchingUserInfo() || this.errorsfetchingUserInfo()) {
        return;
      }

      if (!currentUserInfo
        || currentUserInfo.userIdentifier?.toUpperCase() !== currentUser.userName?.toUpperCase()) {
        return;
      }

      this.patchUpdateUserForm(currentUser.userName, currentUserInfo);
    });
  }

  ngOnDestroy(): void {
    this.lux.destroy(this.luxId);
  }

  ngOnInit(): void {
    this.getUsersList(0);
  }

  private createUpdateUserForm(): UntypedFormGroup {
    return this.fb.group({
      userIdentifier: [{
        value: this.manageUserInfo()?.userName || '',
        disabled: true
      }],
      lastName: ['', [Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.required, Validators.maxLength(50)]],
      firstName: ['', [Validators.pattern('^[a-zA-Z.\'-]+( +[a-zA-Z.\'-]+)*$'), Validators.required, Validators.maxLength(50)]],
      companyName: ['', [Validators.required, Validators.maxLength(150)]],
      emailAddress: [this.manageUserInfo()?.emailAddress || '', [Validators.maxLength(100), Validators.pattern("^[A-Z0-9a-z._%+\\-']+@[A-Za-z0-9.-]+\\.[A-Za-z]{2,}$"), Validators.required]]
    });
  }

  getUsersList(offset: number): void {
    const paginationOffset = this.getUsersListOffset(offset);
    const adminUserSreq = {
      pagination: {
        offset: paginationOffset,
        limit: this.defaultPageSize
      },
      ...this.sortField() && {
        sort: [{
          order: this.sortOrder(),
          fieldName: 'users.' + this.sortField()
        }]
      },
      searchType: 'children'
    };
    this.lux.set(state => state.createManageUserState, { adminUsersReq: adminUserSreq });
    this.createManageUserAsync.getUsersForAdmin()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  getMoreUsers(page) {
    this.currentPage.set(page.pageIndex);
    this.getUsersList(page.pageIndex >= 1 ? (page.pageIndex * this.defaultPageSize) + 1 : 0);
  }

  sortTheUserList(field) {
    const currentSortField = this.sortField();
    let nextSortOrder: 'ASC' | 'DESC' = 'DESC';

    if (field === currentSortField) {
      nextSortOrder = this.sortOrder() !== 'ASC' ? 'ASC' : 'DESC';
    }

    this.sortOrder.set(nextSortOrder);
    if (field !== currentSortField) {
      this.sortField.set(field);
    }
    this.getUsersList(this.currentPage() >= 1 ? (this.currentPage() * this.defaultPageSize) : 0);

  }

  private getUsersListOffset(offset: number): number {
    if (this.sortField() && this.sortOrder() === 'DESC') {
      const currentPage = this.currentPage();
      const pagedOffset = currentPage >= 1
        ? (currentPage * this.defaultPageSize) + 1
        : this.defaultPageSize;
      return (this.totalUsers() + 1) - pagedOffset;
    }

    if (this.currentPage() >= 1 && offset > this.totalUsers()) {
      return offset - this.totalUsers();
    }

    return offset;
  }

  navigateByAction(action, user) {
    if (action !== 'manage') {
      this.lux.set(state => state.createManageUserState.manageUser, {
        selectedAction: action,
        selectedUserId: user.userName,
        selectedUserType: user.userType || 'User'
      });
      this.router.navigate(['/manage-user']);
    } else {
      this.manageUserInfo.set({ ...user });
      this.openUpdateUserInfo();
    }
  }

  openUpdateUserInfo(): void {
    const currentUser = this.manageUserInfo();
    if (!currentUser) {
      return;
    }

    const req = {
      id: currentUser.userName,
      status: currentUser.userStatus === 'Enabled' ? 'A' : 'I'
    };
    this.lux.set(state => state.createManageUserState.updatingUser, { success: '', errors: '' });
    this.createManageUserAsync.getUserInfo(req)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  updateUserInfo(): void {
    const currentUser = this.manageUserInfo();
    if (!currentUser) {
      return;
    }

    const request = { userIdentifier: currentUser.userName, ...this.getFieldsModified(this.updateUserForm) };
    this.createManageUserAsync.updateUserInfo(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  enableDisableUser(userName: string, disable: boolean): void {
    this.createManageUserAsync.enableDisableUser(userName, disable)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private patchUpdateUserForm(userName: string, userData: IUserInfoObj): void {
    this.updateUserForm.controls.userIdentifier.setValue(userName || '');
    this.updateUserForm.controls.companyName.setValue(userData.companyName || '');
    this.updateUserForm.controls.lastName.setValue(userData.lastName || '');
    this.updateUserForm.controls.firstName.setValue(userData.firstName || '');
    this.updateUserForm.controls.emailAddress.setValue(userData.emailAddress || '');
    Object.values(this.updateUserForm.controls).forEach(control => {
      control.markAsPristine();
      control.markAsUntouched();
    });
    this.updateUserForm.markAsPristine();
    this.updateUserForm.markAsUntouched();
    this.formSnapShot.set(JSON.stringify(this.updateUserForm.value));
  }

  getFieldsModified(form: UntypedFormGroup): Record<string, unknown> {
    const modifiedValues: Record<string, unknown> = {};
    Object.keys(form.controls).forEach(key => {
      const currentControl = form.controls[key];
      if (currentControl.dirty) {
        modifiedValues[key] = currentControl.value;
      }
    });
    return modifiedValues;
  }

  resetUpdateUserForm() {
    this.manageUserInfo.set(null);
    this.updateUserForm.reset();
    this.formSnapShot.set('');
  }
}
